import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Row, Col, Card, Button, Form, Alert, Table, Badge, Pagination } from 'react-bootstrap';
import SimpleControlGestionLayout from '../../components/SimpleControlGestionLayout';
import { useGuias } from '../../../Mantenedores/hooks/useGuias';
import * as FaIcons from 'react-icons/fa';

const BusquedaGuias = () => {
  const { user: currentUser } = useSelector(state => state.auth);
  const {
    loading,
    error,
    success,
    guias,
    pagination,
    handleGetGuias
  } = useGuias();

  const [filters, setFilters] = useState({
    guia_id: '',
    estado: '',
    sub_estado: '',
    fecha_desde: '',
    fecha_hasta: '',
    fecha: '',
    searchGuia: '',
    searchRuta: '',
    searchItem: '',
    searchGuiaItem: '',
    cliente_id: '',
    ruta_id: '',
    local_cliente_id: '',
    tms_id: '',
    usuario_movil_id: '',
    pageSize: 10,
    pageNumber: 1
  });

  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    // Cargar datos iniciales
    handleGetGuias(filters);
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      pageNumber: 1 // Resetear a página 1 cuando cambian filtros
    }));
  };

  const handleApplyFilters = () => {
    handleGetGuias(filters);
  };

  const handleClearFilters = () => {
    const defaultFilters = {
      guia_id: '',
      estado: '',
      sub_estado: '',
      fecha_desde: '',
      fecha_hasta: '',
      fecha: '',
      searchGuia: '',
      searchRuta: '',
      searchItem: '',
      searchGuiaItem: '',
      cliente_id: '',
      ruta_id: '',
      local_cliente_id: '',
      tms_id: '',
      usuario_movil_id: '',
      pageSize: 10,
      pageNumber: 1
    };
    setFilters(defaultFilters);
    handleGetGuias(defaultFilters);
  };

  const handlePageChange = (page) => {
    const newFilters = { ...filters, pageNumber: page };
    setFilters(newFilters);
    handleGetGuias(newFilters);
  };

  const getEstadoColor = (estado) => {
    switch (estado?.toLowerCase()) {
      case 'delivered': return 'success';
      case 'not_delivered': return 'danger';
      case 'on_route': return 'warning';
      case 'create': return 'primary';
      default: return 'secondary';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderPagination = () => {
    if (!pagination || pagination.total_pages <= 1) return null;

    const { current_page, total_pages } = pagination;
    const items = [];
    const maxVisible = 5;

    let startPage = Math.max(1, current_page - Math.floor(maxVisible / 2));
    let endPage = Math.min(total_pages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    // Primera página
    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }
    }

    // Páginas intermedias
    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === current_page}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    // Última página
    if (endPage < total_pages) {
      if (endPage < total_pages - 1) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }
      items.push(
        <Pagination.Item key={total_pages} onClick={() => handlePageChange(total_pages)}>
          {total_pages}
        </Pagination.Item>
      );
    }

    return (
      <div className="d-flex justify-content-center mt-4">
        <Pagination>
          <Pagination.Prev
            disabled={current_page === 1}
            onClick={() => handlePageChange(current_page - 1)}
          />
          {items}
          <Pagination.Next
            disabled={current_page === total_pages}
            onClick={() => handlePageChange(current_page + 1)}
          />
        </Pagination>
      </div>
    );
  };

  return (
    <SimpleControlGestionLayout
      title="Búsqueda de Guías"
      subtitle="Busca y filtra guías específicas por diferentes criterios"
      icon={<FaIcons.FaSearch />}
      iconColor="#764ba2"
      breadcrumbs={[
        { label: 'Búsqueda de Guías' }
      ]}
    >
      {/* Filtros */}
      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <FaIcons.FaFilter className="me-2" />
              Filtros de Búsqueda
            </h5>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? <FaIcons.FaChevronUp /> : <FaIcons.FaChevronDown />}
            </Button>
          </div>
        </Card.Header>

        {showFilters && (
          <Card.Body>
            <Row>
                <Col md={3}>
                <Form.Group>
                  <Form.Label>Fecha</Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.fecha}
                    onChange={(e) => handleFilterChange('fecha', e.target.value)}
                  />
                </Form.Group>
              </Col>
              {/* <Col md={3}>
                <Form.Group>
                  <Form.Label>ID Guía</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ingrese ID de guía..."
                    value={filters.guia_id}
                    onChange={(e) => handleFilterChange('guia_id', e.target.value)}
                  />
                </Form.Group>
              </Col> */}
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Estado</Form.Label>
                  <Form.Select
                    value={filters.estado}
                    onChange={(e) => handleFilterChange('estado', e.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="delivered">Entregado</option>
                    <option value="not_delivered">No Entregado</option>
                    <option value="on_route">En Ruta</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              {/* <Col md={3}>
                <Form.Group>
                  <Form.Label>Sub Estado</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Buscar sub estado..."
                    value={filters.sub_estado}
                    onChange={(e) => handleFilterChange('sub_estado', e.target.value)}
                  />
                </Form.Group>
              </Col> */}
              {/* <Col md={3}>
                <Form.Group>
                  <Form.Label>Fecha Desde</Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.fecha_desde}
                    onChange={(e) => handleFilterChange('fecha_desde', e.target.value)}
                  />
                </Form.Group>
              </Col> */}
            </Row>

            <Row className="mt-3">
              {/* <Col md={3}>
                <Form.Group>
                  <Form.Label>Fecha Hasta</Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.fecha_hasta}
                    onChange={(e) => handleFilterChange('fecha_hasta', e.target.value)}
                  />
                </Form.Group>
              </Col> */}
              
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Buscar Guía</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Buscar por guía..."
                    value={filters.searchGuia}
                    onChange={(e) => handleFilterChange('searchGuia', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Buscar Item</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Buscar por item..."
                    value={filters.searchItem}
                    onChange={(e) => handleFilterChange('searchItem', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Buscar Guía o Item</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Buscar por guía o item..."
                    value={filters.searchGuiaItem}
                    onChange={(e) => handleFilterChange('searchGuiaItem', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mt-3">
              
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Buscar Ruta</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Buscar por ruta..."
                    value={filters.searchRuta}
                    onChange={(e) => handleFilterChange('searchRuta', e.target.value)}
                  />
                </Form.Group>
              </Col>
              
              {/* <Col md={3}>
                <Form.Group>
                  <Form.Label>ID Cliente</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="ID del cliente..."
                    value={filters.cliente_id}
                    onChange={(e) => handleFilterChange('cliente_id', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>ID Ruta</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="ID de la ruta..."
                    value={filters.ruta_id}
                    onChange={(e) => handleFilterChange('ruta_id', e.target.value)}
                  />
                </Form.Group>
              </Col> */}
            </Row>

            <div className="d-flex gap-2 mt-3">
              <Button
                variant="primary"
                onClick={handleApplyFilters}
                disabled={loading}
              >
                {loading ? <FaIcons.FaSpinner className="fa-spin me-2" /> : <FaIcons.FaSearch className="me-2" />}
                Buscar
              </Button>
              <Button
                variant="outline-secondary"
                onClick={handleClearFilters}
                disabled={loading}
              >
                <FaIcons.FaTimes className="me-2" />
                Limpiar
              </Button>
            </div>
          </Card.Body>
        )}
      </Card>

      {/* Mensajes de estado */}
      {error && (
        <Alert variant="danger" className="mb-4">
          <FaIcons.FaExclamationTriangle className="me-2" />
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" className="mb-4">
          <FaIcons.FaCheckCircle className="me-2" />
          {success}
        </Alert>
      )}

      {/* Información de paginación */}
      {pagination && (
        <div className="d-flex justify-content-between align-items-center mb-3">
          <small className="text-muted">
            Mostrando {guias?.length || 0} resultados de {pagination.total_records || 0} totales
          </small>
          <small className="text-muted">
            Página {pagination.current_page || 1} de {pagination.total_pages || 1}
          </small>
        </div>
      )}

      {/* Resultados */}
      {loading ? (
        <div className="text-center py-5">
          <FaIcons.FaSpinner className="fa-spin" size={40} />
          <p className="mt-3">Buscando guías...</p>
        </div>
      ) : (
        guias && guias.length > 0 ? (
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <FaIcons.FaList className="me-2" />
                Resultados de Búsqueda
              </h5>
            </Card.Header>
            <Card.Body className="p-0">
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Guía</th>
                    <th>Cliente</th>
                    <th>Centro Despacho</th>
                    <th>Destinatario</th>
                    <th>Ruta Actual</th>
                    <th>Estado Actual</th>
                    <th>Fecha Creación</th>
                    {/* <th>Precio</th> */}
                  </tr>
                </thead>
                <tbody>
                  {guias.map((guia, index) => (
                    <tr key={index}>
                      <td>
                        <strong>{guia.guia}</strong>
                        <br />
                        <small className="text-muted">ID: {guia.guia_id}</small>
                      </td>
                      <td>{guia.cliente?.cliente}</td>
                      <td>{guia.centro_despacho?.centro_despacho}</td>
                      <td>
                        {guia.destinatario?.nombre}
                        <br />
                        <small className="text-muted">{guia.destinatario?.comuna}, {guia.destinatario?.region}</small>
                      </td>
                      <td>
                        {guia.ruta_actual?.ruta}
                        <br />
                        <small className="text-muted">{guia.ruta_actual?.ppu_vehiculo}</small>
                      </td>
                      <td>
                        <Badge bg={getEstadoColor(guia.ultimo_estado?.estado)}>
                          {guia.ultimo_estado?.sub_estado}
                        </Badge>
                        <br />
                        <small className="text-muted">
                          {formatDate(guia.ultimo_estado?.fecha_asignacion)}
                        </small>
                      </td>
                      <td>{formatDate(guia.created_date)}</td>
                      {/* <td>
                        {guia.precio ? `$${guia.precio.toLocaleString('es-CL')}` : '-'}
                      </td> */}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        ) : (
          <div className="text-center py-5">
            <FaIcons.FaInbox size={40} className="text-muted mb-3" />
            <h5>No se encontraron guías</h5>
            <p className="text-muted">Intenta ajustar los filtros de búsqueda</p>
          </div>
        )
      )}

      {/* Paginación */}
      {renderPagination()}
    </SimpleControlGestionLayout>
  );
};

export default BusquedaGuias;
